import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';

import { SearchResultFacetGroup } from '@reverbdotcom/commons/src/gql/graphql';
import { RCText, useMediaQuery } from '@reverbdotcom/cadence/components';
import { Title, LoadingTitle } from '@reverbdotcom/commons/src/components/rql_search_overview';
import { FacetGroupRow } from './facet_group_row';
import ListViewToggle from '@reverbdotcom/commons/src/components/list_view_toggle';

interface IProps {
  facetGroups: SearchResultFacetGroup[];
  loading?: boolean;
  query?: string;
  resultsTotal?: number;
  tipText?: string | React.ReactNode;
  titleFollowText?: string | React.ReactNode;
  toggleListView?: () => void;
}

export default function SearchOverview({
  facetGroups = [],
  loading = false,
  query = '',
  resultsTotal = null,
  tipText = null,
  titleFollowText = '',
  toggleListView = null,
}: IProps) {
  const isTablet = useMediaQuery('tablet');
  const isMobile = useMediaQuery('mobile');
  const classes = classNames(
    'search-overview',
    'search-overview--new',
    { 'search-overview--loading': loading },
  );
  const canRenderQueryTitle = !!query && resultsTotal !== null;
  const showLoadingState = loading && facetGroups.length === 0 && !canRenderQueryTitle;
  const totalText = I18n.t(
    'discovery.resultsCount.countListings',
    { count: resultsTotal, num: I18n.toNumber(resultsTotal, { precision: 0 }) },
  );

  return (
    <nav className={classes}>
      <section className="search-overview__title">
        <div className={classNames(
          'd-flex fx-justify-between fx-align-start gap-2 tablet-mb-1 tablet-pb-1',
          { 'mobile-d-block': !isMobile },
        )}>
          <div>
            {showLoadingState && <LoadingTitle />}
            {!showLoadingState && (
              <>
                <div className={classNames('d-flex fx-wrap', { 'tablet-d-block': !isTablet })}>
                  <RCText.Title
                    size={isMobile ? '500' : '700'}
                    weight={query ? 'regular' : 'bold'}
                    htmlTag="h1"
                  >
                    <Title
                      resultsTotal={resultsTotal}
                      totalText={totalText}
                      query={query}
                    />
                  </RCText.Title>
                  <span className="color-secondary mt-2 ml-2 tablet-ml-0">{titleFollowText}</span>
                  {(isTablet || isMobile) && (
                    <span className="color-secondary d-inline-block tablet-mt-0">{tipText}</span>
                  )}
                </div>
                {!(isTablet || isMobile) && (
                  <span className="color-secondary d-inline-block tablet-mt-0">{tipText}</span>
                )}
              </>
            )}
          </div>
          <div className="d-flex fx-align-end gap-2">
            <SearchOverviewListViewToggle
              loading={loading}
              facetGroups={facetGroups}
              setListViewState={toggleListView}
            />
          </div>
        </div>
      </section>
      <div className={classNames(
        'd-flex fx-dir-col gap-2 mobile-mb-1 scroll-x-on-tablet scroll-x-on-tablet--full-bleed related-searches-pills',
        { 'mobile-pb-4 mobile-bdb-1 bd-primary': !isMobile },
      )}>
        <div className="d-flex fx-justify-between fx-align-start gap-2 ">
          <div>
            <FacetGroupRow
              facetGroups={facetGroups}
              loading={loading}
              query={query}
              resultsTotal={resultsTotal}
            />
          </div>
        </div>
      </div>
    </nav>
  );
}

interface ISearchOverviewListViewToggleProps {
  loading: boolean;
  facetGroups: SearchResultFacetGroup[];
  setListViewState: (val: boolean) => void;
}

function SearchOverviewListViewToggle({ loading, facetGroups, setListViewState }: ISearchOverviewListViewToggleProps) {
  return (
    <div className="search-overview__list-view-toggle">
      {(loading && facetGroups.length === 0) ? (
        <div className="search-overview__list-view-toggle-loading-placeholder" />
      ) :
        (
          <ListViewToggle
            setListViewState={setListViewState}
            loading={loading}
          />
        )}
    </div>
  );
}
